import {
  Dialog,
  DialogBackdrop,
  DialogPanel,
  DialogTitle,
} from "@headlessui/react";
import { XMarkIcon } from "@heroicons/react/24/outline";
import { FC, useContext, useState } from "react";
import { useTranslation } from "react-i18next";
import { BookContext } from "../../contexts/BookContext";
import { PrintModalStep1 } from "./PrintModals/PrintModalStep1";
import { PrintModalStep2 } from "./PrintModals/PrintModalStep2";

type Props = {
  open: boolean;
  setOpen: (isOpen: boolean) => void;
};

export type Step = {
  id: number;
  name: string;
  status: "complete" | "current" | "upcoming";
};

export const PrintModal: FC<Props> = ({ open, setOpen }) => {
  const { t } = useTranslation();
  const { subscription } = useContext(BookContext);

  const [steps, setSteps] = useState<Step[]>([
    { id: 1, name: t("printModal.delivery_address"), status: "current" },
    { id: 2, name: t("printModal.confirmation"), status: "upcoming" },
  ]);

  const currentStep = steps.filter((step) => step.status === "current")[0];

  const resetSteps = () => {
    setSteps(() => [
      { id: 1, name: t("printModal.delivery_address"), status: "current" },
      { id: 2, name: t("printModal.confirmation"), status: "upcoming" },
    ]);
  };

  const nextStep = () => {
    setSteps((prevSteps) => {
      const currentStepIndex = prevSteps.findIndex(
        (step) => step.status === "current"
      );
      if (currentStepIndex === prevSteps.length - 1) {
        return prevSteps;
      }
      return prevSteps.map((step, index) => {
        if (index === currentStepIndex) {
          return { ...step, status: "complete" };
        } else if (index === currentStepIndex + 1) {
          return { ...step, status: "current" };
        } else {
          return step;
        }
      });
    });
  };

  if (subscription?.books_remaining === 0) {
    return (
      <Dialog open={open} onClose={setOpen} className="relative z-10">
        <DialogBackdrop
          transition
          className="fixed inset-0 bg-gray-500/75 transition-opacity data-[closed]:opacity-0 data-[enter]:duration-300 data-[leave]:duration-200 data-[enter]:ease-out data-[leave]:ease-in"
        />

        <div className="fixed inset-0 z-10 w-screen overflow-y-auto">
          <div className="flex min-h-full items-end justify-center p-2 sm:p-4 text-center sm:items-center">
            <DialogPanel
              transition
              className="relative transform overflow-hidden rounded-lg bg-white text-slate-800 px-3 sm:px-4 pb-4 pt-5 text-left shadow-xl transition-all data-[closed]:translate-y-4 data-[closed]:opacity-0 data-[enter]:duration-300 data-[leave]:duration-200 data-[enter]:ease-out data-[leave]:ease-in sm:my-8 sm:w-full sm:max-w-4xl sm:p-6 data-[closed]:sm:translate-y-0 data-[closed]:sm:scale-95 w-full mx-2 sm:mx-0"
            >
              <div className="absolute right-0 top-0 pr-3 pt-3 sm:pr-4 sm:pt-4 block">
                <button
                  type="button"
                  onClick={() => setOpen(false)}
                  className="rounded bg-white text-slate-400 hover:text-slate-500 focus:outline-none focus:ring-2 focus:ring-delicate-500 focus:ring-offset-2"
                >
                  <span className="sr-only">Close</span>
                  <XMarkIcon
                    aria-hidden="true"
                    className="w-5 h-5 sm:w-6 sm:h-6"
                  />
                </button>
              </div>
              <div className="sm:flex sm:items-start">
                <div className="mt-3 text-left sm:ml-4 sm:mt-0 w-full">
                  <DialogTitle
                    as="h3"
                    className="text-xl sm:text-2xl font-semibold text-slate-800 pr-8 sm:pr-0"
                  >
                    {t("printModal.print_book")}
                  </DialogTitle>
                  <hr className="mt-2 w-full border-gray-200" />

                  <p className="mt-4 text-sm sm:text-base text-slate-700 font-semibold">
                    {t("printModal.no_book_remaining")}
                  </p>
                </div>
              </div>
              <div className="mt-5 sm:mt-4 flex flex-col sm:flex-row-reverse gap-3 sm:gap-0">
                <button
                  onClick={() => setOpen(false)}
                  className="inline-flex w-full justify-center rounded bg-rust-50 px-4 py-2 text-sm font-semibold text-white shadow-sm ring-1 ring-inset ring-rust-300 hover:bg-rust-100 sm:w-auto"
                >
                  {t("printModal.close")}
                </button>
              </div>
            </DialogPanel>
          </div>
        </div>
      </Dialog>
    );
  }

  return (
    <>
      {currentStep.id === 1 && (
        <PrintModalStep1
          open={open}
          setOpen={setOpen}
          steps={steps}
          nextStep={nextStep}
        />
      )}
      {currentStep.id === 2 && (
        <PrintModalStep2
          open={open}
          setOpen={setOpen}
          steps={steps}
          resetSteps={resetSteps}
        />
      )}
    </>
  );
};
